<script lang="ts" module>
  import type { Product } from '@agapi/mlc-core';
  import type { CartStore } from '../../../stores';

  export interface ProductInfoActionsProps {
    product: Product;
  }
</script>

<script lang="ts">
  import { getContext } from 'svelte';
  import { ShoppingCartSimple, Check } from "phosphor-svelte";

  import { Button } from '@agapi/ui/button';
  import { QuantitySelector } from '@agapi/ui/quantity-selector';
  import { Loader } from '@agapi/ui/loader';
  import { stack } from '@agapi/ui/panda/patterns';
  import { css } from '@agapi/ui/panda/css';

  import { EVENTS } from '../../../services';
  import { productToCartItem } from '../../../utils/transformer.client';

  import * as m from '$lib/parachute/messages';

  const cart = getContext<CartStore>('cart-store')

  const { product }: ProductInfoActionsProps = $props();

  let stock = $state(product.variants[0].stock);
  let quantity = $state(1);
  let loading = $state(false);
  let added = $state(false);
  let outOfStock = $state(product.variants[0].stock <= 0);

  let buttonRef: HTMLSpanElement | null = null;

  const handleInput = (value: number) => {
    if (Number.isInteger(value) && value > 0) {
      quantity = value;
    }
  }

  const triggerButtonShake = () => {
    buttonRef?.classList.add('shake');
    setTimeout(() => {
      buttonRef?.classList.remove('shake');
    }, 1000);
  }

  const handleCartAdd = async () => {
    if (Number.isInteger(quantity) && quantity > 0) { // Just for safety, is handled in the "handleInput"
      loading = true;

      try {
        const item = cart.items.find(item => item.id === product.id);
        const quantityChange = quantity - (item?.quantity || 0);

        console.log('handleProductCartAdd', { id: product.id, quantity }, quantityChange, Math.abs(quantityChange), );
        await cart.add(productToCartItem({ ...product, id: item?.id ?? product.id }, quantity));
        added = true;

        try {
          EVENTS.emit('cart_item_add', { variant: product.variants[0], quantity: Math.abs(quantityChange) });
        } catch (e) {
          // Ignore error?
        }
      } catch (error: any) {
        if (Number.isInteger(error.stock)) {
          stock = error.stock;
          outOfStock = stock <= 0;
        }
        triggerButtonShake();
      } finally {
        loading = false;
      }
    }
  }
</script>

<style>
  @keyframes shakeAnim {
    0% { transform: translateX(0); }
    20% { transform: translateX(-3px); }
    40% { transform: translateX(6px); }
    60% { transform: translateX(-6px); }
    80% { transform: translateX(3px); }
    100% { transform: translateX(0); }
  }

  .shake {
    position: relative;
    animation: shakeAnim 0.5s ease-in-out;
  }
</style>

<div class={stack({ flexDirection: 'row', gap: '3', maxWidth: 'calc(100dvw - (2 * token(spacing.app)))' })}>
  <QuantitySelector
    onInput={handleInput}
    minValue={product.variants[0].stock > 0 ? 1 : 0}
    maxValue={stock}
    disabled={loading || outOfStock}
    size="sm"
    value={product.variants[0].stock > 0 ? 1 : 0}
    css={{
      pointerEvents: product.variants[0].stock > 0 ? 'unset' : 'none',
      backgroundColor: product.variants[0].stock > 0 ? 'unset' : 'action.disabled.background',
      color: product.variants[0].stock > 0 ? 'unset' : 'action.disabled.foreground',
      maxWidth: '111px'
    }}
  />

  <span bind:this={buttonRef} class={css({width: '100%', display: 'flex', justifyContent: 'stretch'})}>
    <Button
      onclick={handleCartAdd}
      variant="cta"
      css={css.raw({ display: 'flex', width: {base:'100%', md: 'auto'}, minWidth: {base: '200px', md: '240px'}, height: '50px' })}
      disabled={loading || outOfStock}
    >
      {#if loading}
        <Loader css={{ p: 2 }} />
      {:else if added}
        {m.addedToCart()}
        <Check style="bold">
          <title>{m.iconCheck()}</title>
        </Check>
      {:else if outOfStock}
        {m.outOfStock()}
        <ShoppingCartSimple>
          <title>{m.iconShoppingCartAdd()}</title>
        </ShoppingCartSimple>
      {:else}
        {m.addToCart()}
        <ShoppingCartSimple>
          <title>{m.iconShoppingCartAdd()}</title>
        </ShoppingCartSimple>
      {/if}
    </Button>
  </span>
</div>
